/**
 * Order 31 Message Reports contracts (foundation).
 * UI-agnostic; 31B–31D/31G reuse the same filter + row shapes.
 */
import { z } from "zod";
import { CAMPAIGN_STATUSES } from "./campaign-contracts.js";
import { MESSAGE_DIRECTIONS, MESSAGE_ORIGINS, MESSAGE_STATUSES, MESSAGE_TYPES, PLATFORM_TYPES, REPORT_MESSAGE_STATUS_BUCKETS, REPORT_PROCESSING_MESSAGE_STATUSES, } from "./constants.js";
import { apiBigIntIdSchema, apiIntIdSchema } from "./ids.js";
export { REPORT_MESSAGE_STATUS_BUCKETS, REPORT_PROCESSING_MESSAGE_STATUSES, };
export const reportMessageStatusBucketSchema = z.enum(REPORT_MESSAGE_STATUS_BUCKETS);
/** Map a summary/status-card bucket to concrete Message.status values for list filters. */
export function messageStatusesForReportBucket(bucket) {
    switch (bucket) {
        case "PROCESSING":
            return REPORT_PROCESSING_MESSAGE_STATUSES;
        case "SENT":
            return ["SENT"];
        case "DELIVERED":
            return ["DELIVERED"];
        case "READ":
            return ["READ"];
        case "FAILED":
            return ["FAILED"];
        default: {
            const _exhaustive = bucket;
            return _exhaustive;
        }
    }
}
/** Map a Message.status to an Outgoing summary card bucket.
 * Returns null for inbound-only RECEIVED (and would return null for any future
 * status not yet assigned to a card — Total still counts those rows).
 */
export function reportStatusBucketForMessageStatus(status) {
    switch (status) {
        case "PENDING":
        case "SENDING":
            return "PROCESSING";
        case "RECEIVED":
            // Inbound-only in all current write paths; never Outgoing Processing.
            return null;
        case "SENT":
            return "SENT";
        case "DELIVERED":
            return "DELIVERED";
        case "READ":
            return "READ";
        case "FAILED":
            return "FAILED";
        default: {
            const _exhaustive = status;
            return _exhaustive;
        }
    }
}
export const REPORT_MESSAGE_PAGE_SIZES = [25, 50, 100];
export const DEFAULT_REPORT_MESSAGE_PAGE_SIZE = 25;
export const REPORT_MESSAGE_SORTS = ["CREATED_AT_DESC", "CREATED_AT_ASC"];
export const DEFAULT_REPORT_MESSAGE_SORT = "CREATED_AT_DESC";
export const REPORT_MESSAGE_PREVIEW_MAX_CHARS = 120;
export const messageOriginSchema = z.enum(MESSAGE_ORIGINS);
export const messageDirectionSchema = z.enum(MESSAGE_DIRECTIONS);
/**
 * List query. `from`/`to` are ISO-8601 with offset (prefer UTC Z).
 * Semantics: half-open interval [from, to) on Message.createdAt (timestamptz).
 * Callers that want calendar days in Organization.timezone should convert
 * day bounds to UTC Instant ISO strings before calling (see reports date helpers).
 */
/** Shared filter fields (no object-level refinements — Zod 4 blocks `.omit()` otherwise). */
const reportDateYmdSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
    .optional();
const reportMessageFilterFieldsSchema = z.object({
    /** Absolute UTC/offset instants (half-open [from, to)). Prefer fromDate/toDate for UI. */
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    /**
     * Organization-timezone calendar days (inclusive).
     * Interpreted server-side as half-open [fromDate 00:00, toDate+1 00:00) in Organization.timezone.
     */
    fromDate: reportDateYmdSchema,
    toDate: reportDateYmdSchema,
    platform: z.enum(PLATFORM_TYPES).optional(),
    origin: messageOriginSchema.optional(),
    endpointId: z.coerce.number().int().positive().optional(),
    campaignId: z.coerce.number().int().positive().optional(),
    /** Exact Message.messageType filter (Incoming + future Unified Log). */
    messageType: z.enum(MESSAGE_TYPES).optional(),
    /** Exact WhatsApp template name match (structuredData.template.name). */
    template: z
        .string()
        .transform((s) => {
        const t = s.trim();
        return t.length > 0 ? t : undefined;
    })
        .optional(),
    search: z
        .string()
        .max(200)
        .transform((s) => {
        const t = s.trim();
        return t.length > 0 ? t : undefined;
    })
        .optional(),
});
function refineReportDateBounds(value, ctx) {
    if (value.from && value.to) {
        const fromMs = Date.parse(value.from);
        const toMs = Date.parse(value.to);
        if (Number.isFinite(fromMs) && Number.isFinite(toMs) && fromMs >= toMs) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "`from` must be earlier than `to` (half-open [from, to))",
                path: ["from"],
            });
        }
    }
    if ((value.fromDate != null) !== (value.toDate != null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "`fromDate` and `toDate` must be provided together",
            path: value.fromDate == null ? ["fromDate"] : ["toDate"],
        });
    }
    if (value.fromDate && value.toDate && value.fromDate > value.toDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "`fromDate` must be on or before `toDate`",
            path: ["fromDate"],
        });
    }
}
export const reportMessageListQuerySchema = reportMessageFilterFieldsSchema
    .extend({
    direction: messageDirectionSchema.optional(),
    status: z.enum(MESSAGE_STATUSES).optional(),
    /**
     * Mutually exclusive summary bucket filter (Order 31B).
     * When set, overrides `status` so Processing can express PENDING|SENDING.
     */
    statusBucket: reportMessageStatusBucketSchema.optional(),
    page: z.coerce.number().int().positive().optional(),
    pageSize: z.coerce
        .number()
        .int()
        .refine((n) => REPORT_MESSAGE_PAGE_SIZES.includes(n), {
        message: "Invalid page size",
    })
        .optional(),
    sort: z.enum(REPORT_MESSAGE_SORTS).optional(),
})
    .superRefine(refineReportDateBounds);
export const reportMessageEndpointSchema = z.object({
    endpointId: apiIntIdSchema,
    platform: z.enum(PLATFORM_TYPES),
    endpointType: z.string(),
    externalEndpointId: z.string(),
    displayLabel: z.string().nullable(),
});
export const reportMessageContactSchema = z.object({
    contactId: apiIntIdSchema.nullable(),
    displayName: z.string().nullable(),
    normalizedPhoneNumber: z.string().nullable(),
    externalProfileId: z.string(),
    platformDisplayName: z.string().nullable(),
    platformUsername: z.string().nullable(),
});
export const reportMessageCampaignSchema = z.object({
    campaignId: apiIntIdSchema,
    campaignName: z.string(),
    campaignRecipientId: apiBigIntIdSchema.nullable(),
});
export const reportMessageTemplateSchema = z.object({
    whatsappTemplateId: apiIntIdSchema.nullable(),
    name: z.string(),
    languageCode: z.string().nullable(),
});
export const reportMessageListItemSchema = z.object({
    messageId: apiBigIntIdSchema,
    conversationId: apiIntIdSchema,
    direction: messageDirectionSchema,
    platform: z.enum(PLATFORM_TYPES),
    /** Durable origin when known; null for inbound or unclassified historical outbound. */
    origin: messageOriginSchema.nullable(),
    status: z.enum(MESSAGE_STATUSES),
    messageType: z.enum(MESSAGE_TYPES),
    createdAt: z.string().datetime({ offset: true }),
    platformSentAt: z.string().datetime({ offset: true }).nullable(),
    preview: z.string().nullable(),
    failureReason: z.string().nullable(),
    contact: reportMessageContactSchema,
    endpoint: reportMessageEndpointSchema,
    campaign: reportMessageCampaignSchema.nullable(),
    template: reportMessageTemplateSchema.nullable(),
    hasAttachments: z.boolean(),
    /** First non-deleted attachment metadata when present (no URLs / binaries). */
    attachment: z
        .object({
        attachmentType: z.string(),
        fileName: z.string().nullable(),
        mimeType: z.string().nullable(),
    })
        .nullable(),
});
export const reportMessageListResponseSchema = z.object({
    items: z.array(reportMessageListItemSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalCount: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    /** Organization-calendar days used for the list window (echoed for UI date inputs). */
    fromDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    toDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    timezone: z.string().min(1).optional(),
});
/** Summary query = list filters + optional direction (status ignored for distribution). */
export const reportMessageSummaryQuerySchema = reportMessageFilterFieldsSchema
    .extend({
    direction: messageDirectionSchema.optional(),
})
    .superRefine(refineReportDateBounds);
export const reportMessagePlatformCountSchema = z.object({
    platform: z.enum(PLATFORM_TYPES),
    count: z.number().int().nonnegative(),
});
export const reportMessageSummaryResponseSchema = z.object({
    total: z.number().int().nonnegative(),
    /** Outgoing lifecycle buckets (Order 31B). Zeroed for Incoming summaries. */
    processing: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    delivered: z.number().int().nonnegative(),
    read: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /**
     * PlatformType counts for matching rows (Order 31C Incoming + 31D readiness).
     * Always includes every known PlatformType (zeros allowed).
     */
    byPlatform: z.array(reportMessagePlatformCountSchema),
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
    /** Inclusive Organization-timezone calendar day for `from`. */
    fromDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Inclusive Organization-timezone calendar day for the last day covered by [from, to). */
    toDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    timezone: z.string().min(1),
});
export const REPORT_MESSAGE_OPTIONS_LIMIT = 100;
export const reportMessageOptionsQuerySchema = z.object({
    campaignSearch: z
        .string()
        .max(200)
        .transform((s) => {
        const t = s.trim();
        return t.length > 0 ? t : undefined;
    })
        .optional(),
    templateSearch: z
        .string()
        .max(200)
        .transform((s) => {
        const t = s.trim();
        return t.length > 0 ? t : undefined;
    })
        .optional(),
});
export const reportMessageOptionsResponseSchema = z.object({
    platforms: z.array(z.enum(PLATFORM_TYPES)),
    origins: z.array(messageOriginSchema),
    statuses: z.array(z.enum(MESSAGE_STATUSES)),
    statusBuckets: z.array(reportMessageStatusBucketSchema),
    messageTypes: z.array(z.enum(MESSAGE_TYPES)),
    endpoints: z.array(z.object({
        endpointId: apiIntIdSchema,
        platform: z.enum(PLATFORM_TYPES),
        displayLabel: z.string(),
        secondaryLabel: z.string().nullable(),
    })),
    campaigns: z.array(z.object({
        campaignId: apiIntIdSchema,
        name: z.string(),
    })),
    templates: z.array(z.object({
        name: z.string(),
        language: z.string().nullable(),
    })),
});
/** Order 31E — Message Detail Drawer timeline event kinds. */
export const REPORT_MESSAGE_TIMELINE_KINDS = [
    "CREATED",
    "QUEUED",
    "STATUS",
];
export const reportMessageTimelineEventSchema = z.object({
    /** Stable client key (Created / Queued / status-event id). */
    id: z.string().min(1),
    kind: z.enum(REPORT_MESSAGE_TIMELINE_KINDS),
    /** Present for STATUS events; null for CREATED/QUEUED. */
    status: z.enum(MESSAGE_STATUSES).nullable(),
    /** Display timestamp: prefer provider occurrence, else local persist time. */
    occurredAt: z.string().datetime({ offset: true }),
    /** Local MessageStatusEvent.createdAt when different from occurredAt. */
    recordedAt: z.string().datetime({ offset: true }).nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    /** Presentation-only consecutive duplicate collapse count (≥1). */
    duplicateCount: z.number().int().positive().default(1),
});
export const reportMessageDetailAttachmentSchema = z.object({
    attachmentId: apiBigIntIdSchema,
    attachmentType: z.string(),
    fileName: z.string().nullable(),
    mimeType: z.string().nullable(),
    fileSizeBytes: z.string().nullable(),
    storageState: z.string(),
});
export const reportMessageDetailFailureSchema = z.object({
    /** True when Message.status is currently FAILED. */
    isCurrent: z.boolean(),
    code: z.string().nullable(),
    reason: z.string().nullable(),
    occurredAt: z.string().datetime({ offset: true }).nullable(),
    sendJobStatus: z.string().nullable(),
    sendJobAttemptCount: z.number().int().nonnegative().nullable(),
});
export const reportMessageDetailSchema = z.object({
    messageId: apiBigIntIdSchema,
    conversationId: apiIntIdSchema,
    direction: messageDirectionSchema,
    platform: z.enum(PLATFORM_TYPES),
    origin: messageOriginSchema.nullable(),
    status: z.enum(MESSAGE_STATUSES),
    messageType: z.enum(MESSAGE_TYPES),
    createdAt: z.string().datetime({ offset: true }),
    platformSentAt: z.string().datetime({ offset: true }).nullable(),
    /** Full allowed body (not truncated). Null when template/media-only. */
    content: z.string().nullable(),
    /** Safe template presentation when structured template metadata exists. */
    template: reportMessageTemplateSchema
        .extend({
        renderedBody: z.string().nullable(),
    })
        .nullable(),
    contact: reportMessageContactSchema,
    endpoint: reportMessageEndpointSchema,
    campaign: reportMessageCampaignSchema
        .extend({
        relation: z.enum(["CAMPAIGN", "CAMPAIGN_SEND_TEST"]),
    })
        .nullable(),
    attachments: z.array(reportMessageDetailAttachmentSchema),
    timeline: z.array(reportMessageTimelineEventSchema),
    /** Current failure when status=FAILED; otherwise null (historical failures stay in timeline). */
    currentFailure: reportMessageDetailFailureSchema.nullable(),
    technical: z.object({
        messageId: apiBigIntIdSchema,
        conversationId: apiIntIdSchema,
        endpointId: apiIntIdSchema,
        platform: z.enum(PLATFORM_TYPES),
        origin: messageOriginSchema.nullable(),
        externalMessageId: z.string().nullable(),
        externalMessageKey: z.string().nullable(),
        campaignId: apiIntIdSchema.nullable(),
        campaignRecipientId: apiBigIntIdSchema.nullable(),
        clientIdempotencyKey: z.string().nullable(),
        sendJobId: apiBigIntIdSchema.nullable(),
        sendJobStatus: z.string().nullable(),
    }),
    links: z.object({
        conversationPath: z.string().nullable(),
        campaignPath: z.string().nullable(),
        contactId: apiIntIdSchema.nullable(),
    }),
});
// ---------------------------------------------------------------------------
// Order 31F — Campaign Report (aggregate; one row = one Campaign)
// ---------------------------------------------------------------------------
/** Reportable Campaign lifecycle statuses (DRAFT excluded from default dataset). */
export const REPORTABLE_CAMPAIGN_STATUSES = [
    "SCHEDULED",
    "RUNNING",
    "COMPLETED",
    "CANCELLED",
    "FAILED",
];
export const REPORT_CAMPAIGN_PAGE_SIZES = REPORT_MESSAGE_PAGE_SIZES;
export const DEFAULT_REPORT_CAMPAIGN_PAGE_SIZE = DEFAULT_REPORT_MESSAGE_PAGE_SIZE;
export const REPORT_CAMPAIGN_SORTS = [
    "ACTIVITY_AT_DESC",
    "ACTIVITY_AT_ASC",
    "NAME_ASC",
    "NAME_DESC",
    "STATUS_ASC",
    "STATUS_DESC",
    "RECIPIENTS_DESC",
    "DELIVERED_DESC",
    "READ_DESC",
    "FAILED_DESC",
];
export const DEFAULT_REPORT_CAMPAIGN_SORT = "ACTIVITY_AT_DESC";
/**
 * Canonical Campaign Report rates (align with 30F exclusive buckets).
 *
 * FAILED = Message.status FAILED after CampaignRecipient linked a Message
 * (send/delivery attempt outcome — not EXCLUDED, which never had a provider
 * send opportunity).
 *
 * delivery_attempts = sent + delivered + read + failed
 * delivered_or_later = delivered + read
 *
 * Delivery Rate = delivered_or_later / delivery_attempts
 * Read Rate     = read / delivered_or_later
 *
 * Delivery denominator excludes PROCESSING (outcome unknown) and EXCLUDED
 * (no send opportunity). Failed is included so mass-failure Campaigns cannot
 * report near-100% delivery.
 *
 * Read denominator stays delivered_or_later only (Failed/Excluded are not
 * delivery-or-later).
 *
 * Returns null when the denominator is 0 (UI shows "—", not 0%).
 */
export function computeCampaignDeliveryRate(input) {
    const deliveryAttempts = input.sent + input.delivered + input.read + input.failed;
    if (deliveryAttempts <= 0)
        return null;
    return (input.delivered + input.read) / deliveryAttempts;
}
export function computeCampaignReadRate(input) {
    const deliveredOrLater = input.delivered + input.read;
    if (deliveredOrLater <= 0)
        return null;
    return input.read / deliveredOrLater;
}
const reportCampaignFilterFieldsSchema = z.object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    fromDate: reportDateYmdSchema,
    toDate: reportDateYmdSchema,
    /**
     * Campaign lifecycle status. Omit = all reportable non-DRAFT statuses.
     * DRAFT is never included unless includeDrafts=true (API-only; no UI in 31F).
     */
    status: z.enum(REPORTABLE_CAMPAIGN_STATUSES).optional(),
    endpointId: z.coerce.number().int().positive().optional(),
    /** Exact Template name from Campaign.templateSnapshot.name (or seed templateName). */
    template: z.string().trim().min(1).max(200).optional(),
    search: z.string().trim().max(200).optional(),
});
export const reportCampaignListQuerySchema = reportCampaignFilterFieldsSchema.extend({
    page: z.coerce.number().int().positive().optional(),
    pageSize: z.coerce
        .number()
        .int()
        .refine((n) => REPORT_CAMPAIGN_PAGE_SIZES.includes(n))
        .optional(),
    sort: z.enum(REPORT_CAMPAIGN_SORTS).optional(),
});
export const reportCampaignOptionsQuerySchema = z.object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    fromDate: reportDateYmdSchema,
    toDate: reportDateYmdSchema,
});
export const campaignReportSenderSchema = z.object({
    endpointId: apiIntIdSchema,
    displayName: z.string().nullable(),
    phoneNumber: z.string().nullable(),
});
export const campaignReportRowSchema = z.object({
    campaignId: apiIntIdSchema,
    name: z.string(),
    status: z.enum(CAMPAIGN_STATUSES),
    /** COALESCE(startedAt, scheduledAt, createdAt) — canonical report activity instant. */
    activityAt: z.string().datetime({ offset: true }),
    startedAt: z.string().datetime({ offset: true }).nullable(),
    scheduledAt: z.string().datetime({ offset: true }).nullable(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    sender: campaignReportSenderSchema.nullable(),
    templateName: z.string().nullable(),
    templateLanguage: z.string().nullable(),
    /** Same total as Campaign Results summary (exclusive buckets sum). */
    total: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    delivered: z.number().int().nonnegative(),
    read: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
    /** null when delivery_attempts (sent+delivered+read+failed) = 0 */
    deliveryRate: z.number().min(0).max(1).nullable(),
    /** null when delivered_or_later = 0 */
    readRate: z.number().min(0).max(1).nullable(),
    /** Existing Campaign workspace detail route. */
    campaignPath: z.string(),
});
export const reportCampaignListResponseSchema = z.object({
    items: z.array(campaignReportRowSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalCount: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    fromDate: z.string(),
    toDate: z.string(),
    timezone: z.string(),
});
export const reportCampaignOptionsResponseSchema = z.object({
    statuses: z.array(z.enum(REPORTABLE_CAMPAIGN_STATUSES)),
    senders: z.array(campaignReportSenderSchema),
    templates: z.array(z.string()),
    fromDate: z.string(),
    toDate: z.string(),
    timezone: z.string(),
});
/** Order 31G — export format chooser. */
export const REPORT_EXPORT_FORMATS = ["csv", "xlsx"];
export const REPORT_EXPORT_LOCALES = ["en", "he"];
export const reportExportFormatSchema = z.enum(REPORT_EXPORT_FORMATS);
export const reportExportLocaleSchema = z.enum(REPORT_EXPORT_LOCALES);
/**
 * Message export query = list filters + format/locale.
 * page / pageSize are ignored (export all matching rows).
 */
export const reportMessageExportQuerySchema = reportMessageFilterFieldsSchema
    .extend({
    direction: messageDirectionSchema.optional(),
    status: z.enum(MESSAGE_STATUSES).optional(),
    statusBucket: reportMessageStatusBucketSchema.optional(),
    sort: z.enum(REPORT_MESSAGE_SORTS).optional(),
    format: reportExportFormatSchema,
    locale: reportExportLocaleSchema.optional(),
})
    .superRefine(refineReportDateBounds);
export const reportCampaignExportQuerySchema = reportCampaignFilterFieldsSchema.extend({
    sort: z.enum(REPORT_CAMPAIGN_SORTS).optional(),
    format: reportExportFormatSchema,
    locale: reportExportLocaleSchema.optional(),
});
//# sourceMappingURL=report-contracts.js.map